import * as React from 'react';
import styled from 'styled-components';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CalenderIcon } from '@src/components/Icons';
import customParseFormatPlugin from 'dayjs/plugin/customParseFormat';
import localizedFormatPlugin from 'dayjs/plugin/localizedFormat';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
	margin-bottom: 5px;
`;

const StyledDateTimePicker = styled(DateTimePicker)<{
	inputError: string | undefined;
	styles: React.CSSProperties | undefined;
}>`
	height: ${(props) => (props.styles?.height ? props.styles.height : '36px')};
	width: ${(props) => (props.styles?.width ? props.styles.width : '300px')};
	input {
		position: relative;
		border: none;
		outline: none;
		box-sizing: border-box;
		background-color: #fff;
		display: flex;
		padding-left: 10px;
		align-items: center;
	}

	& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid ${(props) => (props.inputError ? 'red' : '#e9e9e9')};
		border-radius: 4px;
	}

	&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid #acacac;
		border-radius: 4px;
	}

	&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid #acacac;
		border-radius: 4px;
	}
`;

const StyledMobileDateTimePicker = styled(MobileDateTimePicker)<{
	inputError: string | undefined;
	styles: React.CSSProperties | undefined;
}>`
	height: ${(props) => (props.styles?.height ? props.styles.height : '36px')};
	width: ${(props) => (props.styles?.width ? props.styles.width : '300px')};
	input {
		position: relative;
		border: none;
		outline: none;
		box-sizing: border-box;
		background-color: #fff;
		display: flex;
		padding-left: 10px;
		align-items: center;
	}

	& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid ${(props) => (props.inputError ? 'red' : '#e9e9e9')};
		border-radius: 4px;
	}

	&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid #acacac;
		border-radius: 4px;
	}

	&.Mui-focused .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
		border: 1.5px solid #acacac;
		border-radius: 4px;
	}
`;

type Props = {
	label?: string;
	inputError?: string | undefined;
	styles?: React.CSSProperties;
	value?: any;
	slots?: any;
	selectedDateTime?: Date | string | null;
	minDateTime?: Date | string | null;
	maxDateTime?: Date | string | null;
	onDateTimeChange: (dateTime: Date | null) => void;
	disabled?: boolean;
	windowDimensions?: any;
	offSetTime?: string;
};

const LabeledDateTimePicker: React.FC<Props> = ({
	label,
	inputError,
	styles,
	disabled,
	windowDimensions,
	...props
}) => (
	<div
		style={{ display: 'flex', flexDirection: 'column', marginTop: '6px', alignItems: 'flex-start' }}
	>
		<StyledLabel>{label}</StyledLabel>
		{windowDimensions?.width > 800 ? (
			<StyledDateTimePicker
				inputError={inputError}
				styles={styles}
				{...props}
				disabled={disabled}
			/>
		) : (
			<StyledMobileDateTimePicker
				slotProps={{
					toolbar: {
						classes: {
							timeDigitsContainer: 'MuiDateTimePickerToolbar-timeDigitsContainer-2',
						},
					},
				}}
				ampmInClock
				inputError={inputError}
				styles={styles}
				{...props}
				disabled={disabled}
			/>
		)}
	</div>
);

dayjs.extend(customParseFormatPlugin);
dayjs.extend(localizedFormatPlugin);
dayjs.extend(isBetweenPlugin);
dayjs.extend(utc);

const CustomDateTimePicker: React.FC<Props> = ({
	label,
	inputError,
	styles = {},
	selectedDateTime,
	onDateTimeChange,
	minDateTime = null,
	maxDateTime = null,
	disabled = false,
	windowDimensions = null,
	offSetTime = '+05:30', //According to Indian Time
}) => {
	const dayjsSelectedDateTime = selectedDateTime
		? dayjs.utc(selectedDateTime).utcOffset(offSetTime)
		: null;
	const dayjsMinDateTime = minDateTime ? dayjs.utc(minDateTime).utcOffset(offSetTime) : null;
	const dayjsMaxDateTime = maxDateTime ? dayjs.utc(maxDateTime).utcOffset(offSetTime) : null;

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<LabeledDateTimePicker
				label={label}
				inputError={inputError}
				styles={styles}
				value={dayjsSelectedDateTime}
				//@ts-ignore
				minDate={dayjsMinDateTime}
				maxDate={dayjsMaxDateTime}
				format="DD-MMMM-YYYY hh:mm A"
				onChange={onDateTimeChange}
				slots={{
					openPickerIcon: CalenderIcon,
				}}
				windowDimensions={windowDimensions}
				disabled={disabled}
			/>
		</LocalizationProvider>
	);
};

export default CustomDateTimePicker;
